import React, { useContext, useState, useEffect } from 'react';
import styled from 'styled-components';

import { ApplicationUIContext, Table } from '@yuhuinc/ui-library';
import { Form, Row, Col, Space, Button, Divider, Alert, DatePicker, Typography, Input } from 'antd';

import { formatMoney } from 'helpers/Formatter';
import { getTranslatedString } from 'helpers/i18n';
import { isAfter, endOfDay } from 'date-fns';
import isEmpty from 'lodash/isEmpty';

const { Title } = Typography;

const StyledContainer = styled.div``;

const StyledFormItem = styled(Form.Item)`
  margin-bottom: 12px;
`;

const StyledForm = styled(Form)`
  padding: 24px;
  background: #fbfbfb;
  border: 1px solid #d9d9d9;
  border-radius: 2px;
`;

const requiredField = {
  required: true,
  message: getTranslatedString('requiredField'),
};

const requiredAncillaryItemField = {
  required: true,
  message: getTranslatedString('tenantPortal.application.parking.required'),
};

const LEASE_ITEM_COLUMNS = [
  {
    title: getTranslatedString('tenantPortal.application.parking.space'),
    dataIndex: 'asset_number',
    fixed: 'left',
  },
  {
    title: getTranslatedString('tenantPortal.application.parking.monthly'),
    dataIndex: 'market_amount',
    render: (text) => formatMoney(text),
  },
  {
    title: getTranslatedString('tenantPortal.application.parking.deposit'),
    dataIndex: 'market_deposit_amount',
    render: (text) => formatMoney(text),
  },
];

const ParkingForm = ({ ancillaryItems = [], initialValues = {}, onAdd, onClose }) => {
  const { isMobile } = useContext(ApplicationUIContext);

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [form] = Form.useForm();
  const [hasError, setError] = useState(false);
  const values = Form.useWatch([], form);

  useEffect(() => {
    if (isEmpty(initialValues)) return;
    setSelectedRowKeys([initialValues.ancillary_item_id]);
  }, [initialValues]);

  const onError = ({ errorFields }) => {
    if (errorFields.length > 1) {
      setError(true);
    }
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys) => {
      setSelectedRowKeys(newSelectedRowKeys);
    },
    onSelect: (record, selected) => {
      form.setFieldsValue({
        market_amount: record.market_amount,
        market_deposit_amount: record.market_deposit_amount,
        asset_number: record.asset_number,
        key_deposit_amount: record.market_key_deposit_amount,
        ancillary_item_id: record.id,
      });
    },
    type: 'radio',
    fixed: true,
  };

  const disabledStartDate = (date) => {
    const startDate = endOfDay(new Date(date));

    const endDate = values?.end_date ? endOfDay(new Date(values.end_date)) : null;

    const isStartDateAfterEndDate = endDate ? isAfter(startDate, endDate) : false;

    return isStartDateAfterEndDate;
  };

  const disabledEndDate = (date) => {
    const startDate = values?.start_date ? endOfDay(new Date(values.start_date)) : null;

    const endDate = endOfDay(new Date(date));

    const isEndDateBeforeStartDate = startDate ? isAfter(startDate, endDate) : false;

    return isEndDateBeforeStartDate;
  };

  return (
    <StyledForm
      layout="vertical"
      size={isMobile && 'large'}
      form={form}
      name="add-parking"
      scrollToFirstError
      onFinish={onAdd}
      onFinishFailed={onError}
      initialValues={initialValues}
    >
      <StyledContainer>
        {hasError && (
          <Alert message={getTranslatedString('errorsOnThisPage')} type="error" showIcon />
        )}

        <Form.Item name="asset_number" noStyle hidden />
        <Form.Item name="market_amount" noStyle hidden />
        <Form.Item name="market_deposit_amount" noStyle hidden />
        <Form.Item name="key_deposit_amount" noStyle hidden />

        <Title level={5}>
          {getTranslatedString('tenantPortal.application.parking.addParking')}
        </Title>

        <StyledFormItem name="ancillary_item_id" rules={[requiredAncillaryItemField]}>
          <Table
            dataSource={ancillaryItems}
            columns={LEASE_ITEM_COLUMNS}
            hideActionAndSearch
            size={isMobile ? 'middle' : 'small'}
            rowSelection={rowSelection}
            pagination={{ pageSize: 10, showSizeChanger: false, hideOnSinglePage: true }}
            scroll={{ x: isMobile }}
            rowKey={(record) => record.id}
          />
        </StyledFormItem>
        <>
          <Row gutter={16}>
            <Col sm={24} md={12}>
              <StyledFormItem
                name="start_date"
                label={getTranslatedString('tenantPortal.application.parking.startDate')}
                rules={[requiredField]}
              >
                <DatePicker style={{ width: '100%' }} disabledDate={disabledStartDate} />
              </StyledFormItem>
            </Col>
            <Col sm={24} md={12}>
              <StyledFormItem
                name="end_date"
                label={getTranslatedString('tenantPortal.application.parking.endDate')}
              >
                <DatePicker style={{ width: '100%' }} disabledDate={disabledEndDate} />
              </StyledFormItem>
            </Col>
          </Row>
        </>

        <Divider />

        <Space>
          <Button type="primary" htmlType="submit">
            {getTranslatedString('add')}
          </Button>

          <Button htmlType="button" onClick={onClose}>
            {getTranslatedString('cancel')}
          </Button>
        </Space>
      </StyledContainer>
    </StyledForm>
  );
};

export default ParkingForm;
